import { clubShowsPlayerBookingDefault, clubShowsScheduleCheckout } from "./clubCapabilities";
import { isClubOwner, isClubStaff, isGuest } from "./UserRoles";

export const clubIncludesCalendar = (selectedClub) => selectedClub?.freePlanUser === true;

export const clubRequiresMemberPlan = (selectedClub) =>
  selectedClub?.isPlanRequired === true || selectedClub?.planRequired === true;

export const clubHasMemberPlans = (products) =>
  Array.isArray(products) && products.some((plan) => {
    const type = String(plan?.planType || "").toLowerCase();
    return type === "subscription" || type === "pay-as-you-go";
  });

export const isCalendarBlockedBySubscription = ({ purchased, userInfo, selectedClub, hasMemberPlans = false }) => {
  if (purchased === true) return false;
  if (isClubOwner(userInfo) || isClubStaff(userInfo)) return false;
  if (clubRequiresMemberPlan(selectedClub) || hasMemberPlans) return true;
  if (clubIncludesCalendar(selectedClub)) return false;
  return true;
};

export const MEMBER_PLAN_REQUIRED_COPY = 'Please purchase a subscription plan to book or create events.';

export const freeReservationDialogCopy = (selectedClub) => (
  clubRequiresMemberPlan(selectedClub)
    ? { title: 'Included in your plan', content: 'This reservation is included in your plan. No checkout needed.' }
    : { title: 'Free reservation', content: 'This is a free reservation. It will be confirmed with no checkout.' }
);

export const shouldShowCalendarSubscriptionMask = ({
  blockedBySubscription,
  userInfo,
  selectedClub,
  hasMemberPlans = false,
}) => {
  if (!blockedBySubscription) return false;
  return isCalendarBlockedBySubscription({
    purchased: false,
    userInfo,
    selectedClub,
    hasMemberPlans,
  });
};

export const isEventCalendarClub = (selectedClub) =>
  String(selectedClub?.calendarType || "").trim().toUpperCase() === "EVENT_CALENDAR";

export const isCourtCalendarClub = (selectedClub) => !isEventCalendarClub(selectedClub);

export const canSetReservationAllDay = (userInfo) =>
  isClubOwner(userInfo) || isClubStaff(userInfo);

export const canConfirmZeroReservation = (userInfo) =>
  isClubOwner(userInfo) || isClubStaff(userInfo);

export const ZERO_COURT_PRICE_COPY =
  "This booking came back as $0.00, so it was not confirmed. The court rate did not match this time.";

export const formatCheckoutMoney = (amount) => {
  const value = Number(amount);
  return Number.isFinite(value) ? value.toFixed(2) : "0.00";
};

const clockMinutes = (raw) => {
  const value = String(raw ?? "").trim().toLowerCase();
  const clock = value.match(/^(\d{1,2})(?::(\d{2}))?\s*(am|pm)$/);
  if (clock) {
    let hour = Number(clock[1]);
    const minute = Number(clock[2] || 0);
    if (hour === 12) hour = 0;
    if (clock[3] === "pm") hour += 12;
    if (hour > 23 || minute > 59) return null;
    return hour * 60 + minute;
  }
  const plain = value.match(/^(\d{1,2}):(\d{2})$/);
  if (!plain) return null;
  const hour = Number(plain[1]);
  const minute = Number(plain[2]);
  if (hour > 23 || minute > 59) return null;
  return hour * 60 + minute;
};

const windowMinutes = (startLabel, endLabel) => {
  const start = clockMinutes(startLabel);
  let end = clockMinutes(endLabel);
  if (start == null || end == null) return null;
  if (end <= start && end === 0) end = 24 * 60;
  if (end <= start) return null;
  return [start, end];
};

const overlapMinutes = (start, end, windowStart, windowEnd) => {
  const from = Math.max(start, windowStart);
  const to = Math.min(end, windowEnd);
  return to > from ? to - from : 0;
};

const rateRows = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
};

const clockParts = (date, utc) => ({
  minutes: (utc ? date.getUTCHours() : date.getHours()) * 60 + (utc ? date.getUTCMinutes() : date.getMinutes()),
  weekend: (utc ? date.getUTCDay() : date.getDay()) === 0 || (utc ? date.getUTCDay() : date.getDay()) === 6,
});

const priceForClock = (checkIn, checkOut, weekday, weekend) => {
  if (!checkIn || !checkOut) return 0;
  let end = checkOut.minutes;
  if (end <= checkIn.minutes && end === 0) end = 24 * 60;
  if (end <= checkIn.minutes) return 0;
  const rows = checkIn.weekend ? weekend : weekday;
  const total = rateRows(rows).reduce((sum, row) => {
    const rate = Number(String(row?.amount ?? "").trim());
    if (!Number.isFinite(rate) || rate <= 0) return sum;
    const window = windowMinutes(row?.startTime ?? row?.start_time, row?.endTime ?? row?.end_time);
    if (!window) return sum;
    const minutes = overlapMinutes(checkIn.minutes, end, window[0], window[1]);
    return sum + (minutes / 60) * rate;
  }, 0);
  return Math.round(total * 100) / 100;
};

/** Same hour pricing the app uses when the payment API returns 0. */
export const courtTimePrice = ({ start, end, weekday = [], weekend = [] } = {}) => {
  const checkIn = new Date(start);
  const checkOut = new Date(end);
  if (Number.isNaN(checkIn.getTime()) || Number.isNaN(checkOut.getTime())) return 0;
  const storedClock = priceForClock(clockParts(checkIn, true), clockParts(checkOut, true), weekday, weekend);
  if (storedClock > 0) return storedClock;
  return priceForClock(clockParts(checkIn, false), clockParts(checkOut, false), weekday, weekend);
};

const samePlanName = (left, right) => {
  const norm = (value) => String(value ?? "").toLowerCase().replace(/[\s_-]+/g, "");
  const a = norm(left);
  const b = norm(right);
  return a.length > 0 && a === b;
};

export const purchasedHourlyPlan = (plans, { productId, planName } = {}) => {
  const hourly = (Array.isArray(plans) ? plans : []).filter(planChargesByTheHour);
  if (productId) {
    const match = hourly.find((plan) => plan?.planId === productId || plan?.productId === productId);
    if (match) return match;
  }
  if (planName) {
    const named = hourly.find((plan) => samePlanName(plan?.planName, planName));
    if (named) return named;
  }
  return hourly.length === 1 ? hourly[0] : null;
};

export const courtTableAmount = (reservations, plan) => {
  if (!plan) return "0.00";
  const total = (Array.isArray(reservations) ? reservations : []).reduce((sum, reservation) => (
    sum + courtTimePrice({
      start: reservation?.reservationCheckIn,
      end: reservation?.reservationCheckOut,
      weekday: plan.weekdayInfo,
      weekend: plan.weekendInfo,
    })
  ), 0);
  return total.toFixed(2);
};

/**
 * Price the booking from the plan the member bought.
 * Subscription product ids are Stripe ids, while plan ids are club plan ids, so a name match counts.
 * When several plans sell court time, the caller's subscription list is tried in order.
 */
export const courtTableAmountFromSubscriptions = (reservations, plans, subscriptions = []) => {
  const list = (Array.isArray(subscriptions) ? subscriptions : [subscriptions]).filter(Boolean);
  for (const subscription of list) {
    const plan = purchasedHourlyPlan(plans, {
      productId: subscription?.productId,
      planName: subscription?.planName,
    });
    const amount = courtTableAmount(reservations, plan);
    if (Number(amount) > 0) return amount;
  }
  const priced = (Array.isArray(plans) ? plans : [])
    .filter(planChargesByTheHour)
    .map((plan) => courtTableAmount(reservations, plan))
    .filter((amount) => Number(amount) > 0);
  if (priced.length === 1) return priced[0];
  if (priced.length > 1 && new Set(priced).size === 1) return priced[0];
  return "0.00";
};

/** Payment API total when it priced the booking. Otherwise the plan's hourly table, like the app. */
export const courtCheckoutAmount = ({ apiAmount, tableAmount } = {}) => {
  const api = Number(String(apiAmount ?? "").trim());
  const table = Number(String(tableAmount ?? "").trim());
  const value = Number.isFinite(api) && api > 0 ? api : (Number.isFinite(table) ? table : 0);
  return value.toFixed(2);
};

/** Prefer the API total. If that is 0, use the rate × hours already listed on checkout. */
export const checkoutChargeAmount = (apiTotal, breakdownRows = []) => {
  const api = Number(apiTotal);
  if (Number.isFinite(api) && api > 0) {
    return api.toFixed(2);
  }
  const sum = (Array.isArray(breakdownRows) ? breakdownRows : []).reduce((total, row) => {
    const rate = Number(row?.price);
    const minutes = Number(row?.hours);
    if (!Number.isFinite(rate) || !Number.isFinite(minutes) || minutes <= 0) {
      return total;
    }
    return total + (rate * (minutes / 60));
  }, 0);
  return sum.toFixed(2);
};

const hourlyRateRows = (plan) => [
  ...rateRows(plan?.weekdayInfo),
  ...rateRows(plan?.weekendInfo),
];

export const planChargesByTheHour = (plan) =>
  hourlyRateRows(plan).some((row) => Number(row?.amount) > 0);

export const reservationChargesCourtTime = ({ selectedClub, scheduleCheckout, products } = {}) => (
  clubShowsScheduleCheckout(selectedClub)
  || scheduleCheckout === true
  || (Array.isArray(products) && products.some(planChargesByTheHour))
);

export const defaultReservationIsFree = (selectedClub, existingIsFree, chargesCourtTime = false) => {
  if (existingIsFree !== undefined) {
    return existingIsFree === true;
  }
  if (chargesCourtTime || clubShowsScheduleCheckout(selectedClub)) {
    return false;
  }
  return true;
};

export const clubAllowsPlayerReservation = (club) => clubShowsPlayerBookingDefault(club);

export const clubShowsReservationPayment = (club) => clubShowsScheduleCheckout(club);

export const clubShowsHeaderCart = (club, userInfo) => (
  Boolean(userInfo?.userId)
  && !isGuest(userInfo)
  && isCourtCalendarClub(club)
);

/** Person charged on create-payment-intent. The logged-in creator is paymentCreatedBy, not this id. */
export const reservationPaymentForUserId = (userInfo, reservations) => (
  reservations?.[0]?.players?.[0] || userInfo?.userId
);

export const reservationCalendarView = (courts, emptyView = 'Day') => (
    Array.isArray(courts) && courts.length > 0 ? 'TimelineDay' : emptyView
);

export const reservationBoardView = (courts) => reservationCalendarView(courts, 'Day');

export const schedulerBoardView = () => 'Day';

export const schedulerActiveView = (currentView) => (
    currentView === 'Week' || currentView === 'Month' ? currentView : 'Day'
);

export const schedulerMonthMaxEvents = () => 3;

export const schedulerShowsCourtGroup = (courts) => false;
